import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { PasswordStrength } from "@/components/PasswordStrength";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Building2, ShieldCheck, AlertTriangle, Sparkles, MessagesSquare, Workflow, Loader2, Mail, ArrowLeft, CheckCircle2, Megaphone, Vote, Home, Landmark, MessageCircle, ClipboardList } from "lucide-react";
import { toast } from "sonner";

const FEATURES = [
  { icon: Mail, text: "Posta ed email con lettura, riassunto e classificazione AI degli allegati" },
  { icon: ClipboardList, text: "Pratiche AI: apertura automatica, priorità e fornitori suggeriti" },
  { icon: MessagesSquare, text: "Chat AI operativa che conosce documenti, avvisi e sondaggi" },
  { icon: Megaphone, text: "Bacheca avvisi ai condòmini con conferme di lettura" },
  { icon: Vote, text: "Sondaggi e votazioni online con risultati in tempo reale" },
  { icon: Home, text: "Stima immobili (AVM) con confronto di zona su dati OMI" },
  { icon: Landmark, text: "Sincronizzazione catastale e visure degli immobili" },
  { icon: MessageCircle, text: "Notifiche SMS per credenziali portali, avvisi e pratiche" },
  { icon: ShieldCheck, text: "Portali condòmini con documenti, avvisi e segnalazioni" },
  { icon: Workflow, text: "Workflow e automazioni: le azioni sensibili restano da approvare" },
];

function formatApiErrorDetail(detail) {
  if (detail == null) return "Si è verificato un errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [denied, setDenied] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null); // {type, text}
  const [needVerify, setNeedVerify] = useState(false);
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("denied")) { setDenied(true); toast.error("Accesso negato: account non autorizzato."); }
    else if (p.get("error")) { toast.error("Autenticazione non riuscita. Riprova."); }
  }, []);

  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const switchMode = (m) => { setMode(m); setNotice(null); setNeedVerify(false); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setNotice(null); setNeedVerify(false);
    const base_url = window.location.origin;
    try {
      if (mode === "login") {
        const { data } = await api.post("/auth/login", { email, password });
        setUser(data.user);
        toast.success("Accesso effettuato");
        navigate("/");
      } else if (mode === "register") {
        const { data } = await api.post("/auth/register", { email, password, name, base_url, privacy_consent: consent, terms_consent: consent });
        setNotice({ type: "success", text: data.message });
      } else if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email, base_url });
        setNotice({ type: "success", text: data.message });
      }
    } catch (err) {
      const status = err?.response?.status;
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
      if (mode === "login" && status === 403 && /confermare/i.test(msg)) setNeedVerify(true);
      setNotice({ type: "error", text: msg });
    } finally { setBusy(false); }
  };

  const resendVerification = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/resend-verification", { email, base_url: window.location.origin });
      setNotice({ type: "success", text: data.message });
      setNeedVerify(false);
    } catch { toast.error("Impossibile inviare l'email"); } finally { setBusy(false); }
  };

  const titles = {
    login: { kicker: "Benvenuto", title: "Accedi a Sedio", sub: "Meno caos, più controllo: l'AI lavora, tu decidi." },
    register: { kicker: "Inizia gratis", title: "Crea il tuo account", sub: "Registrati con email e password. Ti invieremo un link di conferma." },
    forgot: { kicker: "Recupero", title: "Password dimenticata", sub: "Inserisci la tua email: ti invieremo un link per reimpostarla." },
  }[mode];

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-background">
      {/* Left — brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-[#0A0A0A] text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1649989184479-73ab3b750a73?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(140deg, rgba(0,123,255,0.28) 0%, rgba(10,10,10,0.4) 45%, rgba(10,10,10,0.95) 100%)" }} />
        <div className="absolute -top-24 -right-24 h-80 w-80 rounded-full" style={{ background: "radial-gradient(circle, rgba(0,123,255,0.35), transparent 70%)" }} />
        <div className="relative fade-in">
          <div className="flex items-center gap-2.5">
            <img src="/sedio-logo.png" alt="Sedio" className="h-11 w-auto object-contain drop-shadow-lg" />
            <div>
              <div className="font-head text-xl font-extrabold tracking-tight leading-none">Sedio</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mt-1">L'AI a servizio del condominio</div>
            </div>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-head text-4xl xl:text-5xl font-extrabold tracking-tighter leading-[1.05]">
            L'<span className="text-[#4DA3FF]">AI</span> a servizio del condominio.
          </h1>
          <p className="text-white/55 mt-4 max-w-md leading-relaxed">Sedio legge le comunicazioni, riconosce il condominio, apre le pratiche, valuta l'urgenza e tiene informati i condòmini. Tu supervisioni.</p>
          <div className="mt-8 space-y-2.5 max-w-md">
            {FEATURES.map(({ icon: Icon, text }, i) => (
              <div key={i} className="flex items-start gap-3 fade-in" style={{ animationDelay: `${0.08 + i * 0.05}s` }}>
                <div className="h-8 w-8 rounded-lg bg-white/10 border border-white/10 flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-[#4DA3FF]" /></div>
                <span className="text-sm text-white/75 pt-1.5">{text}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-white/30 text-xs">© {new Date().getFullYear()} Sedio · Tutti i diritti riservati</div>
      </div>

      {/* Right — auth forms */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm fade-in">
          <div className="lg:hidden flex items-center gap-2.5 mb-10">
            <img src="/sedio-logo.png" alt="Sedio" className="h-11 w-auto object-contain" />
            <div className="font-head text-xl font-extrabold tracking-tight">Sedio</div>
          </div>

          <div className="text-xs uppercase tracking-[0.2em] text-[#007BFF] font-semibold mb-2">{titles.kicker}</div>
          <h2 className="font-head text-3xl font-bold tracking-tight" data-testid="auth-title">{titles.title}</h2>
          <p className="text-muted-foreground mt-2 text-sm">{titles.sub}</p>

          {denied && (
            <div data-testid="access-denied-banner" className="flex items-start gap-2 mt-5 p-3 rounded-md border border-red-200 bg-red-50 text-red-800 text-sm">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /><span>Accesso non riuscito. Riprova.</span>
            </div>
          )}

          {notice && (
            <div data-testid="auth-notice" className={`flex items-start gap-2 mt-5 p-3 rounded-md border text-sm ${notice.type === "success" ? "border-[#007BFF]/30 bg-[#007BFF]/5 text-[#0056B3]" : "border-red-200 bg-red-50 text-red-800"}`}>
              {notice.type === "success" ? <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" /> : <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />}
              <span>{notice.text}</span>
            </div>
          )}

          {needVerify && (
            <Button variant="outline" className="w-full mt-3 h-10 text-sm" data-testid="resend-verification-btn" onClick={resendVerification} disabled={busy}>
              <Mail className="h-4 w-4 mr-2" />Reinvia email di verifica
            </Button>
          )}

          <form onSubmit={submit} className="mt-6 space-y-3" data-testid="auth-form">
            {mode === "register" && (
              <Input data-testid="auth-name" placeholder="Nome e cognome (facoltativo)" value={name} onChange={(e) => setName(e.target.value)} className="h-11" />
            )}
            <Input data-testid="auth-email" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" />
            {mode !== "forgot" && (
              <PasswordInput data-testid="auth-password" required placeholder={mode === "register" ? "Password (min. 8 caratteri)" : "Password"} value={password} onChange={(e) => setPassword(e.target.value)} className="h-11" />
            )}
            {mode === "register" && <PasswordStrength password={password} />}
            {mode === "login" && (
              <div className="text-right">
                <button type="button" data-testid="goto-forgot" onClick={() => switchMode("forgot")} className="text-xs text-[#007BFF] hover:underline">Password dimenticata?</button>
              </div>
            )}
            {mode === "register" && (
              <label className="flex items-start gap-2 text-[13px] text-muted-foreground cursor-pointer pt-0.5" data-testid="register-consent-label">
                <input type="checkbox" data-testid="register-consent-checkbox" checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-border accent-[#007BFF] shrink-0" />
                <span>Accetto i{" "}
                  <a href="/legale?doc=termini" target="_blank" rel="noopener noreferrer" className="text-[#007BFF] hover:underline">Termini e Condizioni</a>,{" "}
                  l'{" "}
                  <a href="/legale?doc=privacy" target="_blank" rel="noopener noreferrer" className="text-[#007BFF] hover:underline">Informativa Privacy</a>{" "}
                  e l'{" "}
                  <a href="/legale?doc=ai" target="_blank" rel="noopener noreferrer" className="text-[#007BFF] hover:underline">Informativa sull'Intelligenza Artificiale</a>{" "}
                  (Reg. UE 2016/679 e Reg. UE 2024/1689).
                </span>
              </label>
            )}
            <Button type="submit" data-testid="auth-submit" disabled={busy || (mode === "register" && !consent)} className="w-full h-11 rounded-md bg-[#007BFF] hover:bg-[#0056B3] text-white text-base">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "login" ? "Accedi" : mode === "register" ? "Crea account" : "Invia link di reset"}
            </Button>
          </form>

          {mode === "login" && (
            <p className="text-sm text-muted-foreground mt-4 text-center">
              Non hai un account?{" "}
              <button data-testid="goto-register" onClick={() => switchMode("register")} className="text-[#007BFF] font-medium hover:underline">Registrati</button>
            </p>
          )}
          {mode !== "login" && (
            <button data-testid="goto-login" onClick={() => switchMode("login")} className="mt-4 mx-auto flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-3.5 w-3.5" />Torna all'accesso
            </button>
          )}

          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-border" /><span className="text-xs text-muted-foreground">oppure</span><div className="h-px flex-1 bg-border" />
          </div>

          <Button data-testid="google-login-button" onClick={googleLogin} variant="outline"
            className="w-full h-11 rounded-md text-base transition-transform duration-150 hover:-translate-y-0.5">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5 mr-3" />
            Continua con Google
          </Button>

          <div className="flex items-start gap-2 mt-6 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5 text-[#007BFF]" />
            <span>Accesso sicuro. I tuoi dati sono privati e visibili solo al tuo account.</span>
          </div>
          <div className="mt-5 pt-4 border-t border-border flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground" data-testid="login-legal-links">
            <a href="/legale?doc=termini" target="_blank" rel="noopener noreferrer" className="hover:text-foreground hover:underline">Termini e Condizioni</a>
            <a href="/legale?doc=privacy" target="_blank" rel="noopener noreferrer" className="hover:text-foreground hover:underline">Privacy</a>
            <a href="/legale?doc=cookie" target="_blank" rel="noopener noreferrer" className="hover:text-foreground hover:underline">Cookie</a>
            <a href="/legale?doc=ai" target="_blank" rel="noopener noreferrer" className="hover:text-foreground hover:underline">Intelligenza Artificiale</a>
          </div>
        </div>
      </div>
    </div>
  );
}
